import { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
import { useSearchParams } from "react-router-dom";
import AppBackground from "../components/layout/AppBackground";

const Login = () => {
  const theme = useTheme();
  const { t } = useTranslation("login");
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from || "/";
  const { login, isAuthenticated } = useAuth();

  const emailRef = useRef();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
 
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const isValidEmail = (email) => /.+@.+\..+/.test(email);

  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isValidEmail(email)) {
      setError(t("invalid_email"));
      return;
    }

    if (!password || password.length < 4) {
      setError(t("invalid_password"));
      return;
    }

    setLoading(true);
    const API_URL = process.env.REACT_APP_API_URL || "";

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
    if (response.ok) {
      login(data.access_token);

      const redirectTo =
        searchParams.get("redirect") || location.state?.from || "/";

      const pitchType = location.state?.pitchType;
      const aiProfile = location.state?.aiProfile;

      
      // ⏱️ esperamos un toque antes de navegar
      setTimeout(() => {
        navigate(redirectTo, {
          replace: true,
          state: {
            ...(pitchType && { pitchType }),
            ...(aiProfile && { aiProfile }),
          },
        });
      }, 300); // 300-500ms suele sobrar
    } else {
        setError(data.msg || t("login_failed"));
      }
    } catch (err) {
      setError(t("network_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppBackground
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2, // opcional (ya lo tiene el componente)
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        autoComplete="on"
        sx={{
          backgroundColor: "#212121",
          color: "#fff",
          borderRadius: 4,
          p: 4,
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 0 20px rgba(0,0,0,0.5)",
        }}
      >
        <img
          src="/assets/logo-icon.png"
          alt="Pitchest Logo"
          style={{ height: 80, marginBottom: 16 }}
        />
        <Typography variant="h5" gutterBottom>
          {t("login_title")}
        </Typography>

        <TextField
          id="username"
          name="username"
          label={t("email")}
          variant="outlined"
          fullWidth
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ my: 1 }}
          InputProps={{ sx: { height: 40, fontSize: 15 } }}
          InputLabelProps={{ shrink: true, sx: { fontSize: 15 } }}
          inputRef={emailRef}
        />

        <TextField
          id="current-password"
          name="password"
          label={t("password")}
          variant="outlined"
          fullWidth
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          sx={{ my: 1 }}
          InputProps={{
            sx: { height: 40, fontSize: 15 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: "#fff" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true, sx: { fontSize: 15 } }}
        />

        <Button
          onClick={() => navigate("/forgot-password")}
          sx={{
            mt: 1,
            mb: 1,
            color: theme.palette.primary.light,
            textTransform: "none",
          }}
        >
          {t("forgot_password")}
        </Button>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            mt: 2,
            borderRadius: 999,
            py: 1.2,
            "&.Mui-disabled": {
              color: "#cfcbcbcb !important",
            }
          }}      
        >
          {loading ? t("logging_in") : t("login_button")}
        </Button>

        <Button
          onClick={() => navigate("/signup")}
          sx={{ mt: 2, color: theme.palette.primary.light }}
        >
          {t("go_to_signup")}
        </Button>
      </Box>
    </AppBackground>
  );
};

export default Login;
